import { Injectable } from '@angular/core';
import { ConfigDefaults, CsvConfigConsts, Options } from './csv-export.model';
import { TabularReportColumn, TabularReportRow } from 'app/components/app-layout/shared/tabular-report/tabular-report.config';
import moment from 'moment-timezone';
import { toPlainText } from 'app/functions/plain-text';

declare global
{
    interface Navigator
    {
        msSaveBlob?: (blob: any, defaultName?: string) => boolean;
    }
}

/**
 * Leading characters a spreadsheet evaluates no matter what follows them: `=` opens a formula and `@`
 * opens a function call. Same rules as the server-side CsvExportService, so a row is neutralized
 * identically whether the file came from the API or was built here in the browser.
 */
const ALWAYS_FORMULA_TRIGGERS = ['=', '@'];

/**
 * Leading characters that only start a formula when an expression follows.
 *
 * `-` and `+` lead far more prose than formulas in exported cells: `--` is this app's own empty-cell
 * glyph (`emptyCellText`), `-- Operating Cash` is how the chart of accounts marks a child category,
 * and `-$1,234.50` is money. Neutralizing all of those put a visible apostrophe in front of ordinary
 * text, so a value led by these is only neutralized when it also carries FORMULA_SYNTAX.
 */
const CONDITIONAL_FORMULA_TRIGGERS = ['-', '+', '\t', '\r'];

/**
 * Characters that turn a trigger-led value into an expression: a function call, the DDE pipe, or a
 * sheet/cell reference. Every published payload carries at least one — `=cmd|'/C calc'!A0`,
 * `@SUM(1+9)*cmd|'/C calc'!A0`, `-2+3+cmd|'/C calc'!A0` — while a bare `-not a number` yields a
 * `#NAME?` error rather than running anything.
 */
const FORMULA_SYNTAX = /[(|!]/;

/**
 * A number as a spreadsheet reads it, formatted or not: `-$1,234.50`, `(1,234.50)`, `-1,234.50`.
 */
const NUMERIC_NOISE = /[\s$,()]/g;

@Injectable({
    providedIn: 'root',
})
export class CsvExportService
{
    public fileName: string;
    public labels: Array<String>;
    public data: any[];

    private _options: Options;
    private csv = '';

    constructor() {
    }

    /**
     * Generate a CSV from the data format used in the tabular-report component
     */
    public generateTabularReportCsv(
            columns: TabularReportColumn[],
            rows: TabularReportRow[],
            fileName: string,
            indentExport: boolean = false,
            hideZero: boolean = true,
    ) {
        const exportItems = [];
        rows.forEach(row => {
            if (!('export' in row) || row.export) {
                exportItems.push(Object.assign({level: 0}, row));
            }
            if ('children' in row) {
                row.children.forEach(child => {
                    if (!('export' in child) || child.export) {
                        exportItems.push(Object.assign({level: 1}, child));
                    }
                    if ('children' in child) {
                        child.children.forEach(child2 => {
                            if (!('export' in child2) || child2.export) {
                                exportItems.push(Object.assign({level: 2}, child2));
                            }
                            if ('children' in child2) {
                                child2.children.forEach(child3 => {
                                    if (
                                            !('export' in child3) ||
                                            child3.export
                                    ) {
                                        exportItems.push(
                                                Object.assign({level: 3}, child3),
                                        );
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });

        const columnsToExport = columns
                .filter(col => {
                    return !('export' in col) || col.export;
                })
                .map(col => {
                    return String(col.label);
                });

        exportItems.forEach(item => {
            columns.forEach(col => {
                // V2 reports sanitize row keys (dots → underscores) for Material table compatibility,
                // but columns retain the original dotted valueKey. Check both forms.
                const key = col.valueKey in item ? col.valueKey : col.valueKey.replace(/\./g, '_');
                let _default: any = '';
                if (col.format === 'money') {
                    item[key] = item[key] / 100;
                    if (!hideZero) {
                        _default = 0;
                    }
                }
                if (col.format === 'boolean') {
                    item[key] = item[key] ? 'Yes' : 'No';
                }
                item[col.label] = item[key] || _default;
            });
        });

        const options = {
            headers: columnsToExport,
            showLabels: true,
            indent: indentExport,
        };

        this.generateCsv(exportItems, fileName, options);
    }

    /**
     * @param data
     * @param fileName
     * @param fileType
     */
    public downloadFile(data, fileName, fileType) {
        // Create file object for download.
        const blob = new Blob([data], {type: `${fileType};charset:utf-8;`});
        // Get position of the last dot
        const lastPeriodIndex = fileName.lastIndexOf('.');
        // Extract the file name without extension
        const filenameWithoutExtension = fileName.substring(0, lastPeriodIndex);
        // Get file extension
        const fileExtension = fileName.split('.').pop();

        fileName = filenameWithoutExtension + '_' + moment().format('YYYY-MM-DD') + '.' + fileExtension;
        // IE 10+
        if (navigator.msSaveBlob) {
            // navigator.msSaveBlob(blob, `${signee.contract.apiContractTitle.replace(/\s+/g, '')}.pdf`);
            navigator.msSaveBlob(blob, fileName);
        } else {
            const link = document.createElement('a');
            // Feature detection.
            if (link.download !== undefined) {
                // Browsers that support HTML5 download attribute.
                link.href = URL.createObjectURL(blob);
                link.download = fileName;
                link.style.visibility = 'hidden';
                // SD-7474: Chrome does not reliably trigger a download from a detached anchor.
                // Attach the link to the document before dispatching the synthetic click, then
                // remove it once the click has been dispatched so the download fires consistently.
                document.body.appendChild(link);
                link.dispatchEvent(
                        new MouseEvent('click', {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                        }),
                );
                document.body.removeChild(link);
            }
        }
    }

    /**
     * Generate and Download Csv
     */
    public generateCsv(DataJSON: any, filename: string, options?: any) {
        const config = options || {};

        this.csv = '';
        this.data = [];
        this.data = typeof DataJSON !== 'object' ? JSON.parse(DataJSON) : DataJSON;

        if (!filename.endsWith('.csv')) {
            filename += '.csv';
        }

        this._options = objectAssign({}, ConfigDefaults, config);

        if (this._options.filename) {
            this._options.filename = filename;
        }

        if (this._options.useBom) {
            this.csv += CsvConfigConsts.BOM;
        }

        if (this._options.showTitle) {
            this.csv += this._options.title + '\r\n\n';
        }

        this.getHeaders();
        this.getBody();

        if (this.csv === '') {
            console.warn('Invalid data');
            return;
        }

        if (this._options.noDownload) {
            return this.csv;
        }

        this.downloadFile(this.csv, this._options.filename, 'text/csv');
    }

    /**
     * Create Headers
     */
    getHeaders(): void {
        if (this._options.headers.length > 0) {
            const {headers} = this._options;
            let row = headers.reduce((headerRow, header) => {
                let headerValue = header.charAt(0).toUpperCase() + header.slice(1);
                headerValue = headerValue.replace(/"/g, '""');
                if (headerValue.indexOf(',') > -1 || headerValue.indexOf('\n') > -1 || headerValue.indexOf('"') > -1) {
                    headerValue = `"${headerValue}"`;
                }
                return headerRow + headerValue + this._options.fieldSeparator;
            }, '');
            row = row.slice(0, -1);
            this.csv += row + CsvConfigConsts.EOL;
        }
    }

    /**
     * Create Body
     */
    getBody() {
        for (let i = 0; i < this.data.length; i++) {
            let row = '';
            const headerIndices = this._options.headers;
            for (
                    let headerItem = 0;
                    headerItem < headerIndices.length;
                    headerItem++
            ) {
                const t = this.data[i][headerIndices[headerItem]];
                row += this.formatData(t, headerIndices[headerItem]) + this._options.fieldSeparator;
            }
            row = row.slice(0, -1);
            if (this._options.indent && this.data[i].hasOwnProperty('level')) {
                row = '\t'.repeat(this.data[i].level) + row;
                // If this row is a lower indention level than the row above it,
                // insert a number of blank lines equal to the difference in indention levels
                if (i > 0 && this.data[i]['level'] < this.data[i - 1]['level']) {
                    row = (CsvConfigConsts.EOL.repeat(this.data[i - 1]['level'] - this.data[i]['level'])) + row;
                }
            }
            this.csv += row + CsvConfigConsts.EOL;
        }
    }

    /**
     * Format Data
     * @param {any} data
     * @param {string} header
     */
    formatData(data: any, header?: string) {
        if (this._options.decimalseparator === 'locale' && this.isFloat(data)) {
            return data.toLocaleString();
        }

        if (this._options.decimalseparator !== '.' && this.isFloat(data)) {
            return data.toString().replace('.', this._options.decimalseparator);
        }

        if (typeof data === 'string') {
            // Every string column is flattened, not just `comments`: WYSIWYG content reaches plenty of
            // other columns (violation bodies, charge descriptions, form answers) and used to ship its
            // markup raw.
            data = this.formulaSafe(toPlainText(data)).replace(/"/g, '""');
            if (
                    this._options.quoteStrings ||
                    data.indexOf(',') > -1 ||
                    data.indexOf('\n') > -1 ||
                    data.indexOf('\r') > -1
            ) {
                data =
                        this._options.quoteStrings +
                        data +
                        this._options.quoteStrings;
            }
            return data;
        }

        if (this._options.nullToEmptyString) {
            if (data === null) {
                return (data = '');
            }
            return data;
        }

        if (typeof data === 'boolean') {
            return data ? 'TRUE' : 'FALSE';
        }
        return data;
    }

    /**
     * Neutralize CSV formula injection by prefixing a value a spreadsheet would evaluate.
     *
     * Flattening is what makes this necessary rather than nice-to-have: a stored value like
     * `<span>=cmd</span>` used to reach the cell starting with `<`, which no spreadsheet evaluates,
     * and stripping the markup exposes the `=`.
     *
     * Numbers are left alone, formatted or not — a negative amount is a number, not a formula, and
     * prefixing it would turn every negative money cell into text and break downstream sums.
     */
    private formulaSafe(value: string): string {
        if (value === '' || this.isNumeric(value)) {
            return value;
        }

        const first = value.charAt(0);

        if (ALWAYS_FORMULA_TRIGGERS.includes(first)) {
            return `'${value}`;
        }

        return CONDITIONAL_FORMULA_TRIGGERS.includes(first) && FORMULA_SYNTAX.test(value)
                ? `'${value}`
                : value;
    }

    /**
     * Whether a spreadsheet reads the value as a number rather than text.
     *
     * Formatting is stripped before the test because `Number()` rejects every formatted shape, and a
     * formatted negative amount leads with `-` — so without this the guard corrupts exactly the money
     * cells its own rule exists to protect (`alliance-transactions` builds its Amount column with
     * `Intl.NumberFormat`, which renders negatives as `-$1,234.50`).
     */
    private isNumeric(value: string): boolean {
        const bare = value.replace(NUMERIC_NOISE, '');

        return bare !== '' && !isNaN(Number(bare));
    }

    /**
     * Check if is Float
     * @param {any} input
     */
    public isFloat(input: any) {
        return +input === input && (!isFinite(input) || Boolean(input % 1));
    }
}

const hasOwnProperty = Object.prototype.hasOwnProperty;
const propIsEnumerable = Object.prototype.propertyIsEnumerable;

/**
 * Convet to Object
 * @param {any} val
 */
function toObject(val: any) {
    if (val === null || val === undefined) {
        throw new TypeError(
                'Object.assign cannot be called with null or undefined',
        );
    }
    return Object(val);
}

/**
 * Assign data  to new Object
 * @param {any}   target
 * @param {any[]} ...source
 */
function objectAssign(target: any, ...source: any[]) {
    let from: any;
    const to = toObject(target);
    let symbols: any;

    for (let s = 1; s < arguments.length; s++) {
        from = Object(arguments[s]);

        for (const key in from) {
            if (hasOwnProperty.call(from, key)) {
                to[key] = from[key];
            }
        }

        if ((<any>Object).getOwnPropertySymbols) {
            symbols = (<any>Object).getOwnPropertySymbols(from);
            for (let i = 0; i < symbols.length; i++) {
                if (propIsEnumerable.call(from, symbols[i])) {
                    to[symbols[i]] = from[symbols[i]];
                }
            }
        }
    }
    return to;
}
